import { RouteConfig, Route } from 'vue-router';
import { RouteName } from '../routes.constants';
import {
    modalGuard,
    authenticationGuard,
    topNavigation,
    AuthRestriction,
    PassThrough,
    lightTopNavigation,
    CorexUserRestriction,
    IModalMeta,
    config,
} from '../../services';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import { WalletViewRouteName } from '@gig/magic-wallet-types';
import { Jurisdiction } from '@gig/magic-jurisdictions';

const validDate = `\\d{4}\\-\\d{2}\\-\\d{2}`;

const getWalletComponent =
    (component: keyof typeof import('./cashier.chunk')) => async () => {
        const chunk = await import(
            /* webpackChunkName: "cashier" */
            './cashier.chunk'
        );
        return chunk[component];
    };

export const WalletRoutes: RouteConfig[] = [
    {
        path: '',
        component: PassThrough,
        meta: {
            ...authenticationGuard(AuthRestriction.AUTH_REQUIRED),
            ...topNavigation({
                isLight: true,
                showBack: true,
                showBalance: false,
            }),
        },
        children: [
            getHistoryRoute('wallet.title', {
                uniqueModalId: 'wallet',
                options: {
                    backArrow: false,
                },
                desktopOnly: (route: Route) =>
                    route.name !== RouteName.GAMEPLAY,
            }),
            {
                path: ':view?/:method?',
                name: RouteName.WALLET,
                component: getWalletComponent('CashierPage'),
                meta: {
                    ...lightTopNavigation('wallet.title'),
                    ...modalGuard(
                        getWalletComponent('CashierPage'),
                        'wallet.title',
                        {
                            options: {
                                backArrow: false,
                            },
                            uniqueModalId: 'wallet',
                            desktopOnly: (route: Route) =>
                                route.name !== RouteName.GAMEPLAY,
                        },
                    ),
                    extractPathFromRoute: (route: Route) => route.params.view,
                    allowedPathsPerCountry: {
                        [Country.NETHERLANDS]: [
                            WalletViewRouteName.WITHDRAW,
                            WalletViewRouteName.DEPOSIT,
                            WalletViewRouteName.BONUSES,
                        ],
                    },
                    allowedPathsPerJurisdiction: {
                        [Jurisdiction.ONJN]: [
                            WalletViewRouteName.DEPOSIT,
                            WalletViewRouteName.BONUSES,
                        ],
                    },
                    corexUserRestriction: (route: Route) => {
                        if (config.wallet?.skipAccessRestrictedPopup) {
                            return undefined;
                        }
                        return route.params.view ===
                            WalletViewRouteName.WITHDRAW
                            ? CorexUserRestriction.WITHDRAW
                            : CorexUserRestriction.DEPOSIT;
                    },
                },
            },
        ],
    },
];

export function getHistoryRoute(
    title: string,
    options: Partial<IModalMeta['modal']>,
) {
    return {
        name: RouteName.TRANSACTION_HISTORY,
        path: `history/:type(gaming|payments|search|bonuses|purchases|redemptions|freecoins)?/:to(${validDate})?/:from(${validDate})?/:paymentHistoryType(deposits|withdrawals|gaming|bonuses|freecoins|all_payment_transactions)?`,
        component: getWalletComponent('TransactionsPage'),
        meta: {
            ...authenticationGuard(AuthRestriction.AUTH_REQUIRED),
            ...lightTopNavigation(title),
            ...modalGuard(
                getWalletComponent('TransactionsPage'),
                title,
                options,
            ),
            allowedJurisdictions: [Jurisdiction.ONJN],
        },
    };
}
